/// <reference path="../../typings/graphql.d.ts"/>

import * as R from 'ramda'
import moment from 'moment'
import { Cinema, CinemaId, Movie, Show, Times, Days, ImageLink, CINEMA_META, SpecialEvent, SpecialEventDetail, Image, ImageSize } from "./model"
import { lintList, isNotEmpty, isEmpty, lintStringList, replaceAll } from './utils'

export interface CinemaMetadata {
  url: string
  title: string
  description: string
  keywords: string
}

export const gqlToCinema = (cinema: GQL.ICinema): Cinema => {
  const cinemaId = (cinema.slug as CinemaId);
  const movies = cinema.nowShowing == null
    ? []
    : cinema.nowShowing.map(gqlToMovie(cinemaId))
  const comingSoon = cinema.comingSoon == null
    ? []
    : cinema.comingSoon.map(gqlToMovie(cinemaId))
  const extraMovies = cinema.extraMovies == null
    ? []
    : cinema.extraMovies.map(gqlToMovie(cinemaId))

  const banner = cinema.banner == null
    ? null
    : gqlToImageLink(cinema.banner)

  const slides = cinema.slides == null
    ? []
    : cinema.slides.map(gqlToImageLink)

  return {
    id: cinemaId,
    name: cinema.name,
    showSlides: cinema.showSlides,
    heroImage: cinema.heroImage,
    noShowsMessage: cinema.noShowsMessage,
    banner: banner,
    slides: slides,
    movies: movies,
    moviesComingSoon: comingSoon,
    extraMovies: extraMovies,
    topMargin: cinema.topMargin,
    backgroundImage: cinema.backgroundImage,
    description: CINEMA_META[cinemaId].description,
    keywords: CINEMA_META[cinemaId].keywords,
    url: CINEMA_META[cinemaId].url
  }
}

const lintDuration = (duration: string | null): number | null => {
  return duration == null || isNaN(parseInt(duration, 10))
    ? null
    : parseInt(duration, 10)
}

export const gqlToMovie = (cinemaId?: CinemaId) => (movie: GQL.IMovie): Movie => {
  const shows = lintList(movie.schedule).map(gqlToShow(cinemaId, movie.id))
  const event = gqlToEvent(cinemaId, movie)
  const releaseDate = movie.releaseDate == null
    ? null
    : Days.fromMoment(moment(movie.releaseDate, "DD-MM-YYYY"))
  return {
    id: movie.id,
    title: movie.title,
    slug: movie.slug,
    excerpt: movie.exerpt,
    description: movie.exerpt,
    category: lintStringList(movie.genres),
    cast: lintStringList(movie.cast),
    directors: lintStringList(movie.directors),
    duration: lintDuration(movie.duration),
    event,
    trailerUrl: movie.trailerUrl,
    releaseDate: releaseDate,
    picture: movie.background == null ? null : gqlToImage(movie.background),
    poster: gqlToImage(movie.poster!),
    is3D: movie.is3D || false,
    is4K: movie.is4K || false,
    isVM14: movie.isVM14 || false,
    isVM18: movie.isVM18 || false,
    shows: R.groupBy((d => Days.toString(d.day)), shows)
  }
}

export const gqlToImage = (image: GQL.IImage): Image => {
  const sizes: ImageSize[] = image.sizes!.map( size => ({
    width: parseInt(size!.width),
    height: parseInt(size!.height),
    url: adjustImageUrl(size!.url),
    name: size!.name
  }))
  return {
    mainColor: image.dominant_color,
    tinyThumbnail: image.tiny_thumbnail,
    sizes
  }
}

export const gqlToEvent = (cinemaId: CinemaId | undefined, movie: GQL.IMovie): SpecialEvent | null => {
  if (movie.specialEvent == null || isEmpty(movie.specialEvent.event_type)) {
    return null
  }
  return {
    type: movie.specialEvent.event_type!,
    mds: movie.specialEvent.mds == null ? null : {
      dates: lintStringList(movie.specialEvent.mds.dates).map(Days.fromString),
      dateSummary: movie.specialEvent.mds.dates_summary,
      tickets: lintStringList(movie.specialEvent.mds.tickets)
    },
    odeon6: movie.specialEvent.odeon6 == null ? null : {
      dates: lintStringList(movie.specialEvent.odeon6.dates).map(Days.fromString),
      dateSummary: movie.specialEvent.odeon6.dates_summary,
      tickets: lintStringList(movie.specialEvent.odeon6.tickets)
    }
  }
}

export const gqlToShow = (cinemaId: CinemaId | undefined, movieId: string | undefined) => (show: GQL.IShow): Show => {
  return {
    id: '',
    screen: '',
    day: Days.fromMoment(moment(show.date, "DD-MM-YYYY")),
    time: Times.fromString(show.time),
    tags: lintStringList(show.tags).map(replaceAll('-', ' ')).map(replaceAll('_', ' ')),
    cinemaId: cinemaId || (show.cinema && show.cinema.slug as CinemaId),
    movieId: movieId || (show.movie && show.movie.id)
  }
}

export const gqlToImageLink = (imageLink: GQL.IImageLink): ImageLink => {
  return {
    image: gqlToImage(imageLink.image),
    link: imageLink.link
  }
}

const adjustImageUrl = (imageUrl: string): string => {
  const bucket = (imageUrl.length % 3) + 1
  return imageUrl
    .replace('://www', '://m' + bucket)
    .replace('://admin', '://m' + bucket)
}
